import { DictType } from "@pma/types";
import { FINANCIAL_SERVER_URL, RETURN_BILL_ENDPOINT } from "@pma/utils/api-routes";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { parseCookies } from "nookies";
import { Dispatch, SetStateAction, useState } from "react";
import useTranslator from "@pma/hooks/use-translator";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog";
import { toast } from "@pma/hooks/use-toast";
import { Button } from "../common/button";
import { Label } from "@components/common/label";
import { Textarea } from "@components/common/textarea";
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers";

export type ReturnBillContext = "return_to_constructors" | "return_to_supervisor";
interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<any>>;
  bill: DictType;
  context: ReturnBillContext;
  revalidate: CallableFunction;
}
const ReturnBillDialog = ({ bill, context, revalidate, onOpenChange, open }: Props) => {
  const [reason, setReason] = useState("");
  const { translate, locale } = useTranslator();
  const navigate = useNavigate();
  const [searchParam] = useSearchParams();

  const isHebrew = locale === "he";

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const returnedTo = context === "return_to_constructors" ? "Contractor" : "Supervisor";

  const returnBill = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + RETURN_BILL_ENDPOINT,
      {
        bill_id: bill?.id,
        bill_message_text: reason,
        reason: context,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  };

  const handleReturnBillMutation = useMutation({
    mutationKey: ["return-bill", bill?.id],
    mutationFn: returnBill,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("bill returned successfully") + " — " + translate(context === "return_to_constructors" ? "returned to contractor for review" : "returned to supervisor for review"),
        variant: "success",
      });

      revalidate();
      const newParams = new URLSearchParams(searchParam);
      newParams.set("revalidate", "bills");
      newParams.set("bill_tab", context === "return_to_constructors" ? "contractor" : "unsigned");
      navigate(`?${newParams.toString()}`);
      handleCloseDialog();
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "return bill", translate),
        variant: "destructive",
      });
    },
  });

  const handleCloseDialog = () => {
    onOpenChange(undefined);
  };

  return (
    <Dialog open={!!open} onOpenChange={handleCloseDialog}>
      <DialogContent dir={isHebrew ? "rtl" : "ltr"}>
        <DialogHeader>
          <DialogTitle className={cn(isHebrew ? "text-right" :"text-left")}>
            {translate("Return the bill")} {translate(`to ${returnedTo}`)}
          </DialogTitle>
          <DialogDescription className={cn(isHebrew ? "text-right" :"text-left")}>
            <strong>#{bill?.number}</strong> {translate(`will be returned to`)}{" "}
            <strong className=''>{bill?._created_by?.name}</strong>
            <div className='grid gap-2 mt-3'>
              <Label htmlFor='adjustment-reason'>{translate("Reason")}</Label>
              <Textarea
                id='adjustment-reason'
                placeholder={translate("Provide comment") + "..."}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='gap-2'>
          <DialogClose asChild>
            <Button size={"sm"} className=''>
              {translate("Cancel")}
            </Button>
          </DialogClose>

          <Button
            size={"sm"}
            className='bg-destructive'
            onClick={() => handleReturnBillMutation.mutate()}
            disabled={handleReturnBillMutation.isPending}
            loading={handleReturnBillMutation.isPending}
          >
            {translate("Return Bill")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReturnBillDialog;
